import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { chordPcs, judge, rhythmScore, sample, takeOnsets } from "./practice";
import { api } from "../../utils/api";
import auth from "../../utils/auth";
import { track } from "../../utils/track";

/**
 * 연주 체크 · 채점 (2026-10, 새 디자인). 재생 중에는 지금 코드를 맞게 치는지 실시간으로 보여 주고,
 * 재생을 멈추면 코드 정확도(70%)와 박자(30%)로 점수를 내서 기록하고,
 * 이 곡을 연주한 사람들의 평균 점수와 내 순위를 보여 준다.
 */
interface IPracticeCheck {
  musicId: number;
  isPlaying: boolean;
  progress: number; // 악보 진행 값 (마디당 100, 한 박 25)
  chord: string; // 지금 울려야 하는 코드(표시 HTML)
  bar: number; // 지금 마디(0부터)
  songChords: string[]; // 이 곡에 나오는 코드들
  error: string;
  onClose: () => void;
}

type Live = "idle" | "quiet" | "ok" | "miss";

interface IResult {
  score: number;
  chord: number;
  rhythm: number | null;
  bars: number[];
  saved: "saving" | "saved" | "guest" | "short" | "error";
  players?: number;
  average?: number | null;
  rank?: number | null;
  myBest?: number | null;
  recent?: { score: number; created_at: string }[];
}

const MIN_SEC = 20;

const PracticeCheck: React.FC<IPracticeCheck> = ({ musicId, isPlaying, progress, chord, bar, songChords, error, onClose }) => {
  const [live, setLive] = useState<Live>("idle");
  const [result, setResult] = useState<IResult | null>(null);
  const stats = useRef<Map<number, { ok: number; total: number }>>(new Map());
  const recent = useRef<boolean[]>([]);
  const beats = useRef<number[]>([]);
  const startedAt = useRef(0);
  const cur = useRef({ chord, bar, changedAt: 0 });
  if (cur.current.chord !== chord || cur.current.bar !== bar) {
    cur.current = { chord, bar, changedAt: performance.now() };
  }
  // 곡 코드 목록은 화면이 다시 그려질 때마다 새 배열로 오므로 내용이 같으면 그대로 (채점이 중간에 초기화되지 않게)
  const songKey = songChords.join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const songPcs = useMemo(() => Array.from(new Set(songChords)).map(chordPcs).filter((p) => p.length), [songKey]);
  const songPcsRef = useRef(songPcs);
  songPcsRef.current = songPcs;

  // 박이 바뀐 시각 (박자 채점 기준)
  useEffect(() => {
    if (isPlaying && progress % 25 === 0) beats.current.push(performance.now());
  }, [progress, isPlaying]);

  const finish = () => {
    let ok = 0;
    let total = 0;
    const bad: [number, number][] = [];
    stats.current.forEach((v, k) => {
      ok += v.ok;
      total += v.total;
      if (v.total >= 5 && v.ok / v.total < 0.5) bad.push([k, v.ok / v.total]);
    });
    const playedSec = startedAt.current ? (performance.now() - startedAt.current) / 1000 : 0;
    const hits = takeOnsets();
    const beatTimes = beats.current;
    stats.current = new Map();
    recent.current = [];
    beats.current = [];
    startedAt.current = 0;
    if (total < 30) return; // 거의 안 쳤으면 결과 없음
    bad.sort((a, b) => a[1] - b[1]);
    const chordPct = Math.round((ok / total) * 100);
    const rhythm = rhythmScore(beatTimes, hits);
    const score = Math.round(rhythm === null ? chordPct : chordPct * 0.7 + rhythm * 0.3);
    const base: IResult = {
      score,
      chord: chordPct,
      rhythm,
      bars: bad.slice(0, 3).map(([k]) => k + 1).sort((a, b) => a - b),
      saved: !auth.getToken() ? "guest" : playedSec < MIN_SEC ? "short" : "saving",
    };
    setResult(base);
    track("practice:score");
    if (base.saved !== "saving") return;
    api
      .post("/practice/score", { music_id: musicId, score, chord: chordPct, rhythm: rhythm ?? chordPct, played_sec: Math.round(playedSec) })
      .then(({ data }) => {
        const d = data?.data || {};
        setResult({ ...base, saved: "saved", players: d.players, average: d.average, rank: d.rank, myBest: d.my_best, recent: d.recent });
      })
      .catch(() => setResult({ ...base, saved: "error" }));
  };

  useEffect(() => {
    if (!isPlaying) {
      finish();
      setLive("idle");
      return;
    }
    setResult(null);
    takeOnsets();
    beats.current = [];
    startedAt.current = performance.now();
    const timer = setInterval(() => {
      const { chord: c, bar: b, changedAt } = cur.current;
      const pcs = chordPcs(c);
      if (!pcs.length) return;
      const chroma = sample();
      if (!chroma) {
        recent.current = [];
        setLive("quiet");
        return;
      }
      if (performance.now() - changedAt < 250) return; // 코드 바뀐 직후는 손 옮기는 시간
      const ok = judge(chroma, pcs, songPcsRef.current);
      const s = stats.current.get(b) || { ok: 0, total: 0 };
      s.total++;
      if (ok) s.ok++;
      stats.current.set(b, s);
      recent.current = [...recent.current.slice(-4), ok];
      const okCount = recent.current.filter(Boolean).length;
      setLive(okCount * 2 >= recent.current.length ? "ok" : "miss");
    }, 100);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  let text = "재생을 누르고 연주해 보세요";
  if (error) text = error;
  else if (live === "quiet") text = "소리가 잘 안 들려요";
  else if (live === "ok") text = "맞아요";
  else if (live === "miss") text = "다른 코드 같아요";

  const top = result?.rank && result.players ? Math.max(1, Math.round((result.rank / result.players) * 100)) : null;

  return (
    <>
      <Badge state={error ? "miss" : live} data-practice-chord={chord}>
        <Dot state={error ? "miss" : live} />
        <span>
          연주 체크 · {text}
          {live !== "idle" && !error && chord && <Chord dangerouslySetInnerHTML={{ __html: chord }} />}
        </span>
        <Close type="button" aria-label="연주 체크 끄기" onClick={onClose}>
          ✕
        </Close>
      </Badge>

      {result && !isPlaying && (
        <Dim onClick={() => setResult(null)}>
          <Sheet role="dialog" aria-label="연주 점수" onClick={(e) => e.stopPropagation()}>
            <Grip />
            <ScoreRow>
              <Ring style={{ ["--p" as any]: result.score }}>
                <b>{result.score}</b>
                <small>점</small>
              </Ring>
              <Parts>
                <Part>
                  <span>코드</span>
                  <Bar><i style={{ width: `${result.chord}%` }} /></Bar>
                  <em>{result.chord}</em>
                </Part>
                <Part>
                  <span>박자</span>
                  <Bar><i style={{ width: `${result.rhythm ?? 0}%` }} /></Bar>
                  <em>{result.rhythm ?? "–"}</em>
                </Part>
                {result.rhythm === null && <Note>박자는 기타를 친 순간이 적어서 빼고 계산했어요</Note>}
              </Parts>
            </ScoreRow>

            {result.saved === "saved" && result.players ? (
              <Stats>
                <Stat>
                  <span>이 곡 평균</span>
                  <b>{result.average}점</b>
                  <small>{result.players}명 연주</small>
                </Stat>
                <Stat>
                  <span>내 순위</span>
                  <b>{result.rank}등</b>
                  <small>{top !== null ? `상위 ${top}%` : ""}</small>
                </Stat>
                <Stat>
                  <span>내 최고</span>
                  <b>{result.myBest}점</b>
                  <small>{result.score >= (result.myBest || 0) ? "새 기록!" : ""}</small>
                </Stat>
              </Stats>
            ) : (
              <Info>
                {result.saved === "saving" && "기록하는 중…"}
                {result.saved === "guest" && "로그인하면 점수가 기록되고, 다른 사람들과 순위를 비교할 수 있어요"}
                {result.saved === "short" && `${MIN_SEC}초 이상 연주하면 점수가 기록돼요`}
                {result.saved === "error" && "점수를 기록하지 못했어요. 잠시 뒤 다시 해 주세요"}
              </Info>
            )}

            {result.bars.length > 0 && <Info>다시 볼 마디: {result.bars.join(", ")}</Info>}

            {result.recent && result.recent.length > 1 && (
              <History>
                <span>최근 기록</span>
                <div>
                  {result.recent.map((r, i) => (
                    <i key={i} title={r.created_at}>{r.score}</i>
                  ))}
                </div>
              </History>
            )}

            <Ok type="button" onClick={() => setResult(null)}>
              확인
            </Ok>
          </Sheet>
        </Dim>
      )}
    </>
  );
};

export default PracticeCheck;

const COLOR: Record<Live, string> = { idle: "#7c8392", quiet: "#7c8392", ok: "#3ddc97", miss: "#ffb547" };

const Badge = styled.div<{ state: Live }>`
  position: fixed;
  left: 50%;
  top: 96px;
  transform: translateX(-50%);
  z-index: 120;
  max-width: calc(100% - 32px);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 14px;
  border-radius: 20px;
  background: rgba(22, 24, 30, 0.96);
  border: 1px solid ${({ state }) => COLOR[state]};
  color: #f3f4f6;
  font-size: 13px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  & > span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Dot = styled.i<{ state: Live }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background: ${({ state }) => COLOR[state]};
`;

const Chord = styled.em`
  font-style: normal;
  margin-left: 6px;
  color: #ffb547;
  font-weight: 700;
  & sup {
    font-size: 0.7em;
  }
`;

const Close = styled.button`
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 0;
  background: #2a2e38;
  color: #f3f4f6;
  font-size: 11px;
  cursor: pointer;
`;

const Dim = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1200;
  background: rgba(5, 6, 9, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 520px;
  box-sizing: border-box;
  border-radius: 24px 24px 0 0;
  background: #16181e;
  border-top: 1px solid #2a2e38;
  padding: 10px 20px calc(20px + env(safe-area-inset-bottom));
  color: #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Grip = styled.div`
  align-self: center;
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #3a3f4b;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

// 점수 원: --p(0~100) 만큼 호박색으로 채움
const Ring = styled.div`
  flex: none;
  width: 108px;
  height: 108px;
  border-radius: 54px;
  background: conic-gradient(#ffb547 calc(var(--p) * 1%), #2a2e38 0);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 9px;
    left: 9px;
    right: 9px;
    bottom: 9px;
    border-radius: 50%;
    background: #16181e;
  }
  & > b {
    position: relative;
    font-size: 38px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
  & > small {
    position: relative;
    margin: 14px 0 0 2px;
    font-size: 14px;
    color: #a3a9b5;
  }
`;

const Parts = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Part = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  & > span {
    flex: none;
    width: 30px;
    color: #a3a9b5;
  }
  & > em {
    flex: none;
    width: 26px;
    text-align: right;
    font-style: normal;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

const Bar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #2a2e38;
  overflow: hidden;
  & > i {
    display: block;
    height: 100%;
    background: #5b8cff;
    border-radius: 3px;
  }
`;

const Note = styled.p`
  margin: 0;
  font-size: 11px;
  color: #7c8392;
  word-break: keep-all;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 4px;
  border-radius: 14px;
  background: #0d0e12;
  border: 1px solid #2a2e38;
  & > span {
    font-size: 12px;
    color: #a3a9b5;
  }
  & > b {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  & > small {
    min-height: 15px;
    font-size: 11px;
    color: #3ddc97;
  }
`;

const Info = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #a3a9b5;
  word-break: keep-all;
`;

const History = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #a3a9b5;
  & > div {
    display: flex;
    gap: 6px;
  }
  & i {
    font-style: normal;
    padding: 3px 8px;
    border-radius: 8px;
    background: #2a2e38;
    color: #f3f4f6;
    font-variant-numeric: tabular-nums;
  }
  & i:first-of-type {
    background: #2a2416;
    color: #ffb547;
  }
`;

const Ok = styled.button`
  height: 50px;
  border: 0;
  border-radius: 14px;
  background: #5b8cff;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
`;
